/**
 * utils/index.ts
 * Pure helper functions — no side effects, no imports from store or API.
 */

/** Strip HTML tags to plain text */
export const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Truncate text to n characters */
export const truncate = (text: string, n: number): string =>
  text.length > n ? text.slice(0, n).trimEnd() + '…' : text;

/** Format bytes to MB string */
export const formatMb = (mb: number): string =>
  mb >= 1000 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;

/** Generate a random hex color from the Jötr palette */
const PALETTE = ['#F5A623','#C4811A','#16A34A','#7C3AED','#DB2777','#2563EB','#0891B2','#DC2626'];
export const randomColor = (): string => PALETTE[Math.floor(Math.random() * PALETTE.length)];

/** Get initials from a full name */
export const getInitials = (name: string): string =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

/** Debounce any function */
export const debounce = <T extends (...args: unknown[]) => void>(fn: T, ms: number): T => {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
};

/** Highlight all occurrences of query in text, wrapping with <mark> */
export const highlight = (text: string, query: string): string => {
  if (!query.trim()) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
};

/** Sort notes — pinned first, then by updatedAt desc */
export const sortNotes = (notes: { isPinned: boolean; updatedAt: string }[]) =>
  [...notes].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
